/**
 * row-layout — on va cada reserva dins de la seva fila (`plans/calendar-visual-refresh.md` §5, F1).
 * Funcions pures, amb tests.
 *
 * - **Vores tallades (P1):** `clipStart` / `clipEnd` diuen a la card que la reserva continua fora
 *   del rang visible (la card ho pinta, F2).
 * - **Cancel·lades (D6, P2):** una cancel·lada mai tapa una viva. Si la fila en té alguna de visible,
 *   les vives s'enfilen i les cancel·lades van en una franja baixa de tot. Els px de cada lloc
 *   depenen de l'alçada de la fila (`rowSlotStyle`, F7).
 * - **Línies de la card (F7):** `cardLines` / `cardDense` segons l'alçada de la card al seu lloc.
 * - **Posició horitzontal (D4, §9bis, F6):** arribades i sortides a mig dia
 *   (`calculateReservationPosition`), per a les cards vives, la franja de cancel·lades i el fantasma.
 *
 * Dates `YYYY-MM-DD` (les ISO de la BD es tallen als 10 primers caràcters, com a `resolve-targets`).
 */

import { addDays, daysBetween, toDay } from './resolve-targets';

/** El rang de dies que es veu: `start` = primer dia visible, `endExclusive` = l'endemà de l'últim. */
export interface VisibleRange {
  start: string;
  endExclusive: string;
}

/** El mínim d'una reserva per decidir el seu lloc a la fila. */
export interface RowLayoutStay {
  id: string;
  checkIn: string;
  checkOut: string;
  status?: string | null;
}

/**
 * - `full`: la fila no té cap cancel·lada visible; la reserva ocupa l'alçada de sempre.
 * - `live`: viva en una fila amb cancel·lades; deixa lliure la franja de baix.
 * - `cancelled`: la franja de 10 px de baix de tot.
 */
export type RowSlot = 'full' | 'live' | 'cancelled';

/**
 * Alçada de les files (roda dentada, F7 · en Pere 29-SET): Normal 75 px, Compacta 60 px (per
 * defecte), Mínima 46 px.
 */
export type RowHeightSetting = 'normal' | 'compact' | 'minimal';

export const ROW_HEIGHT_PX: Record<RowHeightSetting, number> = {
  normal: 75,
  compact: 60,
  minimal: 46,
};

/**
 * La mida de fila que toca a una alçada en px qualsevol (la graella en pot rebre una altra: el
 * calendari històric passa 75, i sense `cellHeight` la graella fa 70 → Normal). Talls a mig camí.
 */
export function rowHeightTier(rowHeightPx: number): RowHeightSetting {
  if (rowHeightPx >= 68) return 'normal';
  if (rowHeightPx >= 53) return 'compact';
  return 'minimal';
}

/**
 * Marges verticals (px) de cada lloc, per mida de fila. Vives: `top`/`bottom` des de les vores de
 * la fila. Franja de cancel·lades: `height` a `bottom` px de baix.
 *
 * | Mida     | `full` (dalt/baix) | `live` (dalt/baix) | franja (alçada @ baix) | aire viva–franja |
 * |----------|--------------------|--------------------|------------------------|------------------|
 * | Normal   | 4 / 4              | 4 / 16             | 10 @ 2                 | 4 px             |
 * | Compacta | 2 / 2              | 2 / 10             | 6 @ 2                  | 2 px             |
 * | Mínima   | 2 / 2              | 2 / 8              | 4 @ 2                  | 2 px             |
 */
const SLOT_INSETS: Record<
  RowHeightSetting,
  { full: [number, number]; live: [number, number]; strip: { height: number; bottom: number } }
> = {
  normal: { full: [4, 4], live: [4, 16], strip: { height: 10, bottom: 2 } },
  compact: { full: [2, 2], live: [2, 10], strip: { height: 6, bottom: 2 } },
  minimal: { full: [2, 2], live: [2, 8], strip: { height: 4, bottom: 2 } },
};

/** `style` de posició vertical d'un lloc (dins del contenidor de dies, `relative`). */
export interface RowSlotStyle {
  top?: number;
  bottom: number;
  height?: number;
}

export function rowSlotStyle(rowHeightPx: number, slot: RowSlot): RowSlotStyle {
  const insets = SLOT_INSETS[rowHeightTier(rowHeightPx)];
  if (slot === 'cancelled') return { bottom: insets.strip.bottom, height: insets.strip.height };
  const [top, bottom] = insets[slot];
  return { top, bottom };
}

/**
 * El mateix lloc com a coordenades y des de dalt de la fila (`top` = vora de dalt, `bottom` = vora
 * de baix), per comprovar que una viva i la franja de les cancel·lades no se solapen mai.
 */
export function rowSlotBox(rowHeightPx: number, slot: RowSlot): { top: number; bottom: number } {
  const style = rowSlotStyle(rowHeightPx, slot);
  const bottom = rowHeightPx - style.bottom;
  const top = style.height != null ? bottom - style.height : (style.top ?? 0);
  return { top, bottom };
}

/** Alçada (px) de la card en aquest lloc (vores incloses). */
export function cardBoxHeight(rowHeightPx: number, slot: RowSlot): number {
  const box = rowSlotBox(rowHeightPx, slot);
  return box.bottom - box.top;
}

/** Des d'aquesta alçada de card (px), 3 línies; per sota, 2 (F7). */
export const CARD_THREE_LINES_MIN_PX = 50;
/** Per sota d'aquesta alçada de card (px), la card es compacta (padding i nom més petits). */
export const CARD_DENSE_BELOW_PX = 60;

/**
 * Línies de la card segons l'espai (F7): 3 (nom · `👥 0/2+2 · 1n · ES` · import/llits) si la card
 * fa ≥ 50 px, 2 (nom · import/llits) si no. Normal → 3 sempre (67 / 55 px); Compacta → 3 al lloc
 * `full` (56 px) i 2 al `live` (48 px); Mínima → 2 sempre (42 / 36 px).
 */
export function cardLines(rowHeightPx: number, slot: RowSlot): 2 | 3 {
  if (slot === 'cancelled') return 2;
  return cardBoxHeight(rowHeightPx, slot) >= CARD_THREE_LINES_MIN_PX ? 3 : 2;
}

/**
 * La card es compacta (`py-0.5`, nom a 13 px) si fa < 60 px: Normal `live` (55), Compacta i Mínima.
 * Els comptes d'alçada són a `ReservationCell.tsx`.
 */
export function cardDense(rowHeightPx: number, slot: RowSlot): boolean {
  return cardBoxHeight(rowHeightPx, slot) < CARD_DENSE_BELOW_PX;
}

/** Mida màxima (px) d'un llum de la card (`plans/calendar-card-signals.md` §6 F1.3). */
export const LIGHT_MAX_PX = 16;

/**
 * Mida (px) dels tres llums de la dreta de la card: `min(16, floor((caixa − 10) / 3))`. Els 10 px són
 * les dues vores (2), els dos espais entre llums (4) i un marge de 2 a dalt i a baix. Surt Normal 16/15,
 * Compacta 15/12, Mínima 10/8 (`full`/`live`). El símbol només es pinta des de 12 px.
 */
export function cardLightSize(rowHeightPx: number, slot: RowSlot): number {
  return Math.max(0, Math.min(LIGHT_MAX_PX, Math.floor((cardBoxHeight(rowHeightPx, slot) - 10) / 3)));
}

/** El rang visible a partir de les dates de la graella (`null` si no n'hi ha cap). */
export function visibleRangeOf(firstDay: string | null, lastDay: string | null): VisibleRange | null {
  if (!firstDay || !lastDay) return null;
  return { start: toDay(firstDay), endExclusive: addDays(lastDay, 1) };
}

export function isCancelledStay(stay: Pick<RowLayoutStay, 'status'>): boolean {
  return stay.status === 'cancelled';
}

/**
 * L'estada es veu al rang: alguna nit hi cau, o surt el primer dia visible (D4: el matí de la
 * sortida és la meitat esquerra de la primera columna; si no, les sortides d'avui no es veurien
 * mai amb la vista que comença avui). El mateix criteri que la posició de la card.
 */
export function isStayVisible(stay: Pick<RowLayoutStay, 'checkIn' | 'checkOut'>, range: VisibleRange): boolean {
  return toDay(stay.checkOut) >= range.start && toDay(stay.checkIn) < range.endExclusive;
}

/**
 * Clic en una cel·la lliure → dia d'entrada de la reserva nova (D4). Amb les cards a mig dia, la
 * meitat esquerra de la columna del dia `d` és la nit d'abans (d−1 → d) i la meitat dreta, la nit
 * de `d`. `offsetX` = px des de la vora esquerra de la cel·la.
 */
export function resolveDayClick(date: Date, offsetX: number, cellWidth: number): Date {
  if (offsetX >= cellWidth / 2) return date;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1);
}

/**
 * Vores tallades, amb la geometria de mig dia (D4, F6): la card comença a mig dia de l'entrada i
 * acaba a mig dia de la sortida.
 *
 * - `clipStart` = l'entrada és abans del primer dia visible (una entrada el primer dia comença a
 *   mig dia de la primera columna: es veu sencera).
 * - `clipEnd` = el mig dia de la sortida no es veu: la sortida és l'endemà de l'últim dia visible
 *   o més tard. Una sortida just l'endemà acabaria a mig dia d'una columna que no es pinta, i per
 *   això també és tallada (la card arriba a la vora de la graella amb `›`).
 *
 * `calculateReservationPosition` fa servir aquesta mateixa funció: vora tallada i posició no poden
 * dir coses diferents.
 */
export function computeClip(
  stay: Pick<RowLayoutStay, 'checkIn' | 'checkOut'>,
  range: VisibleRange,
): { clipStart: boolean; clipEnd: boolean } {
  return {
    clipStart: toDay(stay.checkIn) < range.start,
    clipEnd: toDay(stay.checkOut) >= range.endExclusive,
  };
}

/** Classes d'arrodoniment d'un marc que ha de casar amb la card (anell de selecció, fantasma). */
export function clipRoundingClasses(stay: { clipStart?: boolean; clipEnd?: boolean }): string {
  return [stay.clipStart && 'rounded-l-none', stay.clipEnd && 'rounded-r-none'].filter(Boolean).join(' ');
}

/**
 * Marge (px) a cada costat de la card: dues reserves que es toquen (canvi d'hoste, una surt i
 * l'altra entra el mateix dia) es veuen com dues. A una vora tallada no n'hi ha (toca la graella).
 */
export const STAY_EDGE_GAP_PX = 1;

/**
 * Posició horitzontal d'una reserva dins de la fila (D4, F6: arribades i sortides a mig dia, el
 * patró de Beds24). `w` = `cellWidth`, `d(x)` = dies des del primer dia visible:
 *
 * - `left = d(checkIn) × w + w/2 + 1`; amb `clipStart`, `left = 0`.
 * - final `= d(checkOut) × w + w/2 − 1`; amb `clipEnd`, final `= dies visibles × w` (la vora de
 *   la graella: mai no en surt).
 *
 * `null` si l'estada no es veu (`isStayVisible`). Una estada que surt el primer dia visible és una
 * card petita al matí de la primera columna (tallada per l'esquerra, amplada `w/2 − 1`). Una
 * sortida que no és posterior a l'entrada (dades dolentes) compta com una nit, com abans.
 */
export function calculateReservationPosition(
  checkIn: string,
  checkOut: string,
  range: VisibleRange,
  cellWidth: number,
): { left: number; width: number } | null {
  const inDay = toDay(checkIn);
  const outDay = toDay(checkOut) > inDay ? toDay(checkOut) : addDays(inDay, 1);
  const stay = { checkIn: inDay, checkOut: outDay };
  if (!isStayVisible(stay, range)) return null;

  const { clipStart, clipEnd } = computeClip(stay, range);
  const half = cellWidth / 2;
  const left = clipStart
    ? 0
    : daysBetween(range.start, inDay) * cellWidth + half + STAY_EDGE_GAP_PX;
  const right = clipEnd
    ? daysBetween(range.start, range.endExclusive) * cellWidth
    : daysBetween(range.start, outDay) * cellWidth + half - STAY_EDGE_GAP_PX;
  return { left, width: right - left };
}

export interface RowLayout {
  /** La fila té alguna cancel·lada visible: les vives van enfilades i hi ha franja a baix. */
  hasCancelledStrip: boolean;
  /** El lloc de cada reserva de la fila, per id. */
  slots: Map<string, RowSlot>;
}

/** Decideix el lloc de cada reserva d'una fila. */
export function computeRowLayout(stays: readonly RowLayoutStay[], range: VisibleRange): RowLayout {
  const hasCancelledStrip = stays.some((s) => isCancelledStay(s) && isStayVisible(s, range));
  const slots = new Map<string, RowSlot>();
  for (const stay of stays) {
    if (isCancelledStay(stay)) slots.set(stay.id, 'cancelled');
    else slots.set(stay.id, hasCancelledStrip ? 'live' : 'full');
  }
  return { hasCancelledStrip, slots };
}
